import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import {
  AccountsApi,
  RequestStrategyEnum,
  UsersApi,
  type AccountRequest,
  type AccountResponse,
  type AddUserAccountsRequest,
  type DeleteUserAccountsRequest,
  type WithSignal,
} from '@shared/api';

const getUserAccountsEffect = createEffect(({ signal, ...request }: WithSignal<AccountRequest>) => AccountsApi.getAccounts(request, signal));
const addAccountsEffect = createEffect(async (request: AddUserAccountsRequest) => {
  await UsersApi.addUserAccounts(request);
  return { AssignedUserId: request.userIds };
});
const deleteAccountEffect = createEffect(async (request: DeleteUserAccountsRequest) => {
  await UsersApi.deleteUserAccounts(request);
  return { AssignedUserId: request.userIds };
});
const clearEvent = createEvent();

sample({ source: addAccountsEffect.doneData, target: getUserAccountsEffect, fn: (doneData) => ({ AssignedUserId: doneData?.AssignedUserId }) });
sample({ source: deleteAccountEffect.doneData, target: getUserAccountsEffect, fn: (doneData) => ({ AssignedUserId: doneData?.AssignedUserId }) });

const $dataStore = createStore<AccountResponse[]>([])
  .on(getUserAccountsEffect.done, (current, data) => data.result.data || current)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getUserAccountsEffect.pending, (_, data) => data)
  .on(getUserAccountsEffect.fail, (_, data) => ApiUtils.abortApiLoadingFailCaseTrigger(data.error))
  .on(getUserAccountsEffect.done, () => false)
  .on(addAccountsEffect.pending, (_, data) => data)
  .on(addAccountsEffect.fail, () => false)
  .on(deleteAccountEffect.pending, (_, data) => data)
  .on(deleteAccountEffect.fail, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getUserAccountsEffect, () => null)
  .on(getUserAccountsEffect.fail, () => 'Something went wrong while fetching Accounts. Please try again.')
  .on(getUserAccountsEffect.done, () => null)
  .reset(clearEvent);

const $actionErrorStore = createStore<string | null>(null)
  .on(addAccountsEffect, () => null)
  .on(
    addAccountsEffect.fail,
    (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while adding Accounts. Please try again.').message,
  )
  .on(addAccountsEffect.done, () => null)
  .on(deleteAccountEffect, () => null)
  .on(
    deleteAccountEffect.fail,
    (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while deleting Account. Please try again.').message,
  )
  .on(deleteAccountEffect.done, () => null)
  .reset(clearEvent);
$actionErrorStore.watch((error) => $notificationStore.events.setError(error));

const getUserAccountsApi = ApiUtils.wrapperApi(getUserAccountsEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => getUserAccountsApi.cancel());

export const $userAccountsStore = {
  events: {
    getUserAccounts: getUserAccountsApi.trigger,
    addAccounts: addAccountsEffect,
    deleteAccount: deleteAccountEffect,
    clear: clearEvent,
  },
  store: combine({
    loading: $loadingStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
